import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4, v4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import Button from 'src/components/button/Button';
import Typography from 'src/components/common/typography/Typography';
import Card from 'src/components/common/card/Card';
import { SelfUserContext } from 'src/pages/myProfile/MyProfile';
import imageCompression from 'browser-image-compression';
import { StorageUtils, textFormatter } from 'src/utils';
import { ChatIcon, EditIcon, MoreOptionsIcon } from 'src/components/common/common.icons';
import {
  DropdownOptionCard,
  TDropdownOptions,
  TDropdown,
  Dropdown,
  ActionIcon,
  Loader,
  ConfirmModal
} from 'src/components/common';
import SubMenuModal from 'src/components/common/subMenuModal/SubMenuModal';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  assignModalId,
  assignSubMenuModalId,
  openPopup,
  resetDropdownId
} from 'src/store/common/common.slice';
import { deleteProfilePicture, getPresignedUrl } from 'src/store/profile/profile.slice';
import { TFindCreateConversationRequest, TFollowRequest, TListOneToOneChatsRequest } from 'src/services/apiEndpoint.types.ts';
import { follow, getUserStatsSuccess, unFollow } from 'src/store/socialMedia/socialMedia.slice';
import { Role } from 'src/constants/user.constants';
import { useGetIdentity, useUrlParamValue } from 'src/hooks';
import { findCreateConversation, listOneToOneChats, listOneToOneChatsSuccess, setLocalChat } from 'src/store/chat/chat.slice';

import './profileCard.scss';

import EditProfile from '../editProfile/EditProfile';
import ProfilePicture from '../profilePicture/ProfilePicture';
import Company from 'src/assets/images/svg/Company.svg';
import Entrepreneur from 'src/assets/images/svg/Entrepreneur.svg';
import MentorBadge from 'src/assets/images/svg/MentorBadge.svg';
import IncubatorBadge from 'src/assets/images/svg/IncubatorBadge.svg';
import LikeUserModal from 'src/components/common/LikeUserModal/LikeUserModal';
import { TLikeUser } from 'src/components/posts/postCard/PostCard';

export type TProfileCard = {
  introduction: string;
  name: string;
  profilePic: string;
  role: string;
  dropdownItems?: TDropdownOptions[];
  userId: string;
  getUserStats?: ({ id }: { id: string }) => void;
};

const editProfileModalId = 'edit-profile-modal';

const ProfileCard = ({
  introduction,
  name,
  profilePic,
  role,
  dropdownItems = [],
  userId,
  getUserStats
}: TProfileCard) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userModalOpen, setUserModal] = useState<boolean>(false)
  const [likedUser, setLikedUser] = useState<TLikeUser[]>([]);
  const [key, setKey] = useState('')
  const uploadImageRef = useRef<HTMLInputElement>(null);

  const isSelfUser = useContext(SelfUserContext); // Whether this profile card is viewed by self.

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getIdentities } = useGetIdentity();
  const { paramValue } = useUrlParamValue();
  const { dropdownId, modalId } = useAppSelector((store) => store.common);
  const userStats = useAppSelector(
    (store) => store.socialMedia.userStats
  );
  const { followerCount, followingCount, postCount, isFollowing, ideaCount } = userStats
  const isAuthorized: boolean = useAppSelector((store) => store.auth.isAuthorized);
  const {
    oneToOneChatLists,
    listOneToOneChatsLoader
  } = useAppSelector((store) => store.chat);
  const { profile, viewUserProfile } = useAppSelector((store) => store.profile);
  const postId: string = paramValue({ paramName: 'postId' }) as string;

  // state
  const [loading, setLoading] = useState<boolean>(false)
  const [uploadLoading, setUploadLoading] = useState<boolean>(false)
  const [show, setShow] = useState<boolean>(false)
  // Constants
  const { authenticatedId, loggedInUserType } = getIdentities();

  const profileStats = [
    { key: 'follower', label: 'Follower', value: followerCount, onclick: handleUserModalOpen },
    { key: 'following', label: 'Following', value: followingCount, onclick: handleUserModalOpen },
    { key: 'post', label: 'Post', value: postCount },
    { key: 'ideas', label: 'Ideas', value: ideaCount }
  ];

  /* eslint-disable no-unused-vars */
  enum ImageFileTypes {
    BMP = 'bmp',
    JPG = 'jpg',
    JPEG = 'jpeg',
    PNG = 'png'
  }

  function handleUserModalOpen(key: string) {
    setKey(key)
    if (key === 'follower' && userStats?.followerUsers?.length > 0) {
      setLikedUser(userStats?.followerUsers)
      setUserModal(true)
    } else if (key === 'following' && userStats?.followingUsers?.length > 0) {
      setLikedUser(userStats?.followingUsers)
      setUserModal(true)
    }
  }

  const openPictureDropdown = () => {
    if (dropdownId) {
      dispatch(resetDropdownId());
    } else {
      dispatch(assignSubMenuModalId('profile-card'));
    }
  };

  const openFileManager = async () => {
    if (uploadImageRef.current) {
      uploadImageRef.current.click();
    }
  };

  const handleOpenModal = () => {
    dispatch(assignModalId(editProfileModalId));
    setIsModalOpen(true);
  };

  const handleUploadProfilePicture = async (e: ChangeEvent<HTMLInputElement>) => {
    const file: File = (e.target.files as FileList)[0];
    if (file) {
      // Compress the image
      const options = {
        maxSizeMB: 0.5, // Maximum size in kb (500kb)
        maxWidthOrHeight: 1024, // Maximum width or height in pixels
        useWebWorker: true
      };
      const compressedFile = await imageCompression(file, options);
      const { name } = compressedFile;
      const fileType = name.split('.').pop() as unknown as 'bmp' | 'jpg' | 'jpeg' | 'png';

      const selectedImageDetail = {
        type: fileType
      };

      const isImageFileType = Object.keys(ImageFileTypes).includes(
        selectedImageDetail.type.toUpperCase()
      );

      if (isImageFileType) {
        setUploadLoading(true);
        setTimeout(() => {
          setUploadLoading(false)
        }, 1000)
        // upload image
        const reader = new FileReader();
        reader.readAsArrayBuffer(compressedFile);

        reader.onload = async () => {
          const data = reader.result;

          dispatch(getPresignedUrl({ email: profile.email, data }));
        };
      } else if (!isImageFileType) {
        dispatch(
          openPopup({
            popupMessage: 'Image should be of type BMP, JPG, JPEG, or PNG',
            popupType: 'error'
          })
        );
      }
    }
    // Resetting the value of target
    const element = e.target as HTMLInputElement;
    element.value = '';
  };

  const handleDeleteProfilePicture = () => {
    dispatch(deleteProfilePicture({ email: profile.email }));
  };

  const dropdownOptions: TDropdown = {
    id: 'profile-card-dropdown',
    size: 'large',
    alignment: 'center',
    items: dropdownItems
  };

  const openProfileCardDropdown = () => {
    if (dropdownId) {
      dispatch(assignSubMenuModalId(''));
    } else {
      dispatch(assignSubMenuModalId(dropdownOptions.id));
    }
  };

  const handleFollow = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    const payload: TFollowRequest = {
      userId: authenticatedId,
      userType: loggedInUserType,
      followUserId: userId,
      followUserType: 'User'
    };
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 1800)

    if (isFollowing) {
      dispatch(unFollow(payload)).then(() => {
        if (getUserStats) {
          getUserStats({ id: userId });
        }
      });
    } else {
      dispatch(follow(payload)).then(() => {
        if (getUserStats) {
          getUserStats({ id: userId });
        }
      });
    }
  };

  const chatToUser = () => {
    if (listOneToOneChatsLoader) {
      return
    }
    if (!isAuthorized) {
      setShow(true)
      return
    }
    if (!isSelfUser && userId) {
      if (oneToOneChatLists?.length === 0) {
        const conversationId = v4()
        const chatPayload: any = {
          Companies: [],
          Users: [viewUserProfile],
          userId,
          deletedAt: null,
          createdAt: new Date().toISOString(),
          groupName: null,
          id: conversationId,
          moduleType: 'General',
          ownerId: userId,
          type: 'DirectChat',
          unreadMessageCount: 0,
          updatedAt: new Date().toISOString(),
          userType: 'User',
          isUninitiated: true
        }
        dispatch(setLocalChat(chatPayload))
        const path = `/chat?conversationId=${conversationId}&userType=User&userId=${userId}&initiated=true&prev=/view-profile?id=${userId}`;
        navigate(path);
      } else {
        const payload: TFindCreateConversationRequest = {
          senderUserId: authenticatedId,
          senderUserType: loggedInUserType,
          receiverUserId: userId,
          receiverUserType: 'User',
          moduleType: 'General'
        };
        dispatch(findCreateConversation(payload)).then((data) => {
          const conversationId = data?.conversation?.id;
          if (conversationId) {
            const path = `/chat?conversationId=${conversationId}&userType=User&userId=${userId}&prev=/view-profile?id=${userId}`;
            navigate(path);
          }
        });
      }
    }
  };
  function handleNavigate() {
    navigate(`/login?prev=/view-profile?id=${userId}&prev=/dashboard?postId=${postId || ''}`)
  }
  const getChatLists = () => {
    const payload: TListOneToOneChatsRequest = {
      userId: authenticatedId,
      userType: loggedInUserType,
      limit: 500,
      offset: 1,
      filterString: viewUserProfile?.name
    };

    dispatch(listOneToOneChats(payload));
  };

  useEffect(() => {
    if (authenticatedId && loggedInUserType && viewUserProfile) {
      getChatLists()
    }
  }, [authenticatedId, loggedInUserType, viewUserProfile])

  return (
    <>
      {userModalOpen && <LikeUserModal title={key} users={likedUser} isModalOpen={userModalOpen} handleCloseModal={() => setUserModal(false)} />}
      <Card className="profile-card">
        <div className="profile-card__picture">
          {!uploadLoading && <ProfilePicture url={profilePic} fallback={textFormatter({ value: name[0] })} />}
          {uploadLoading && <Loader />}
          {isSelfUser && (
            <div
              className="profile-card__picture__edit"
              onClick={(e) => {
                e.stopPropagation();
                openPictureDropdown();
              }}
            >
              <EditIcon />
            </div>
          )}
          {!uploadLoading && <SubMenuModal>
            <div
              data-modal="profile-card"
              className={classNames('profile-card__picture__options', {
                'profile-card__picture__options--is-open': dropdownId === 'profile-card'
              })}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(assignSubMenuModalId('profile-card'));
              }}
            >
              <DropdownOptionCard size="large" onClick={openFileManager}>
                Upload
              </DropdownOptionCard>
              <DropdownOptionCard size="large" onClick={handleDeleteProfilePicture}>
                Delete
              </DropdownOptionCard>
            </div>
          </SubMenuModal>}
          <input
            type="file"
            hidden
            data-testid="inputUploadImage"
            ref={uploadImageRef}
            accept=".bmp,.jpg,.jpeg,.png"
            onChange={handleUploadProfilePicture}
          />
        </div>
        <div className="profile-card__content">
          <div className="profile-card__content__header">
            <div className="profile-card__content__header__user-info">
              <Typography as="h5" variant="subHeading-2" fontWeight="semiBold">
                {textFormatter({ value: name })}
                {(role === Role.ASPIRING_ENTREPRENEUR || role === Role.ENTREPRENEUR) && (<img style={{ marginLeft: "8px", height: "20px", width: "20px", marginBottom: "2px" }} src={Entrepreneur} alt="  Entrepreneur" />)}
                {(role === "Mentor" || role === "Investor") && (<img style={{ marginLeft: "8px", height: "18px", width: "18px", marginBottom: "2px" }} src={MentorBadge} alt="Mentor/Investor" />)}
              </Typography>
              {/* <Typography as="p" variant="body-2" fontWeight="regular">
                {role === Role.SERVICE_PROVIDERS_STARTUP ? '-' : textFormatter({ value: role })}
              </Typography> */}
              {(role !== Role.ASPIRING_ENTREPRENEUR && role !== Role.ENTREPRENEUR) && (<Typography as="p" variant="body-2" fontWeight="regular">
                {textFormatter({ value: role })}
              </Typography>)}
            </div>
            <div className="profile-card__content__header__actions">
              {isSelfUser && (
                <Button variant="secondary" size="extraSmall" onClick={handleOpenModal}>
                  Edit profile
                </Button>
              )}
              {!isSelfUser && (
                <>
                  <Button
                    variant={isFollowing ? 'secondary' : 'primary'}
                    size="extraSmall"
                    className="profile-card__content__header__actions__follow"
                    onClick={handleFollow}
                    disabled={loading}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </Button>{' '}
                  <ActionIcon
                    className="profile-card__content__header__actions__chat"
                    onClick={chatToUser}
                  >
                    <ChatIcon />
                  </ActionIcon>
                  <div
                    className="post-card__header__actions__dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfileCardDropdown();
                    }}
                  >
                    {<MoreOptionsIcon />}{' '}
                    <SubMenuModal>
                      <Dropdown {...dropdownOptions} />
                    </SubMenuModal>
                  </div>
                </>
              )}
            </div>
          </div>
          <Typography
            as="p"
            variant="caption"
            fontWeight="regular"
            className="profile-card__content__introduction"
          >
            {introduction}
          </Typography>
          <div className="profile-card__content__stats">
            {profileStats.map(({ key, label, value, onclick }) => {
              return (
                <div key={key} className="profile-card__content__stats__stat">
                  <div className="profile-card__content__stats__stat__values"
                    onClick={() => onclick && isSelfUser && onclick(key)}
                    style={onclick && isSelfUser ? { cursor: 'pointer' } : {}}
                  >
                    <Typography
                      as="p"
                      variant="caption"
                      fontWeight="regular"
                      className="profile-card__content__stats__stat__values__label"
                    >
                      {label}
                    </Typography>
                    <Typography
                      as="p"
                      variant="body-1"
                      fontWeight="semiBold"
                      className="profile-card__content__stats__stat__values__value"

                    >
                      {value || 0}
                    </Typography>
                  </div>
                  <hr></hr>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      {/*
       ** Edit profile modal
       */}
      {isSelfUser && modalId === editProfileModalId && (
        <EditProfile isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      )}
      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigate}
        submitText="Sign in"
        type="confirm"
      />
    </>
  );
};

export default ProfileCard;
